/**
 * Popup newsletter: si apre dopo un ritardo o a una certa percentuale di scorrimento.
 * Se viene chiuso senza iscriversi ricompare solo in home, al massimo altre due volte (dopo 10 minuti e dopo un giorno);
 * dopo la terza chiusura torna solo trascorsi i giorni impostati nell'editor. Dopo l'iscrizione non ricompare piu.
 * Usa <dialog> (focus trap, Esc e aria-modal nativi). Il codice sconto compare dopo l'iscrizione.
 * localStorage e sempre dentro try/catch: se non disponibile il popup funziona comunque, senza memoria.
 */

const STORAGE_KEY = 'gdm_newsletter_popup';
const DAY_MS = 24 * 60 * 60 * 1000;
// Attese prima di riproporlo in home dopo la 1a e la 2a chiusura; il numero di attese e il numero di riproposte
const REOPEN_GAPS_MS = [10 * 60 * 1000, DAY_MS];
const ALWAYS_EXCLUDED_PREFIXES = ['/cart', '/checkout', '/checkouts', '/account', '/policies', '/password'];

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    /* memoria non disponibile: si ignora */
  }
}

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.formStep = this.querySelector('[data-popup-form-step]');
    this.successStep = this.querySelector('[data-popup-success-step]');
    this.form = this.querySelector('form');
    this.errorMessage = this.querySelector('[data-popup-error]');
    this.copyButton = this.querySelector('[data-popup-copy]');
    this.status = this.querySelector('[data-popup-status]');
    this.opened = false;

    this.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', () => this.rememberClosed());
    this.dialog.addEventListener('close', () => this.rememberClosed());
    this.form?.addEventListener('submit', (event) => this.submit(event));
    // Il captcha di Shopify chiude il suo giro con form.submit(): lo si devia sull'invio in background
    if (this.form) this.form.submit = () => this.send();
    this.copyButton?.addEventListener('click', () => this.copyCode());

    // Design mode: nessun comportamento automatico, il popup si apre solo dall'editor.
    if (window.Shopify?.designMode) return;

    if (this.querySelector('[data-popup-posted]')) {
      // Iscrizione appena inviata (pagina ricaricata dal form senza JavaScript)
      this.showSuccess();
      this.open(true);
      return;
    }

    if (this.dataset.enabled !== 'true' || !this.isAllowed()) return;
    this.scheduleOpen();
  }

  isAllowed() {
    const path = window.location.pathname.toLowerCase();
    if (ALWAYS_EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + '/'))) return false;
    if (/^\/[a-z]{2}(-[a-z]{2})?\/(cart|checkouts?|account|policies)(\/|$)/.test(path)) return false;

    const pageType = this.dataset.pageType || '';
    if (pageType === 'cart' || pageType === 'policy' || pageType === 'password' || pageType.startsWith('customers/')) return false;

    const excluded = (this.dataset.excluded || '').split(/\s+/).filter(Boolean);
    if (excluded.some((part) => path.includes(part.toLowerCase()))) return false;

    const state = readState();
    if (state?.status === 'subscribed') return false;
    if (state?.status === 'closed') {
      const elapsed = Date.now() - Number(state.at);
      if (elapsed >= this.reopenMs()) return true;
      const count = Number(state.count) || 1;
      if (count > REOPEN_GAPS_MS.length || pageType !== 'index') return false;
      return elapsed >= REOPEN_GAPS_MS[count - 1];
    }
    return true;
  }

  reopenMs() {
    return (Number(this.dataset.reopenDays) || 30) * DAY_MS;
  }

  scheduleOpen() {
    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    const scrollPercent = Math.max(0, Number(this.dataset.scroll) || 0);
    let timer = null;

    const trigger = () => {
      cleanup();
      this.openWhenCookieBannerGone();
    };
    const onScroll = () => {
      const scroller = document.querySelector('.page-wrapper');
      const container = scroller && scroller.scrollHeight > scroller.clientHeight ? scroller : document.scrollingElement;
      const max = container.scrollHeight - container.clientHeight;
      if (max > 0 && (container.scrollTop / max) * 100 >= scrollPercent) trigger();
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll, true);
    };

    timer = window.setTimeout(trigger, delay);
    if (scrollPercent > 0) window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  }

  /** Non copre il banner cookie di Shopify: aspetta che sia stato chiuso (massimo 60 secondi). */
  openWhenCookieBannerGone() {
    const started = Date.now();
    const check = () => {
      if (this.opened) return;
      const banner = document.getElementById('shopify-pc__banner');
      const visible = banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden';
      if (!visible) {
        this.open(false);
      } else if (Date.now() - started < 60000) {
        window.setTimeout(check, 500);
      }
    };
    check();
  }

  open(isSuccess) {
    if (this.opened || !this.dialog || this.dialog.open) return;
    this.opened = true;
    this.closeRecorded = false;
    this.previousFocus = document.activeElement;
    this.dialog.showModal();
    // Il focus parte dal pannello (non dal pulsante di chiusura) per non mostrare un contorno subito
    if (isSuccess) this.successStep?.focus();
    else this.dialog.focus({ preventScroll: true });
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  rememberClosed() {
    // 'cancel' (Esc) e 'close' arrivano entrambi: si conta una sola chiusura per apertura
    if (this.subscribed || this.closeRecorded) return;
    this.closeRecorded = true;
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus();
    const previous = readState();
    const recent = previous?.status === 'closed' && Date.now() - Number(previous.at) < this.reopenMs();
    writeState({ status: 'closed', at: Date.now(), count: recent ? (Number(previous.count) || 1) + 1 : 1 });
  }

  showSuccess() {
    this.subscribed = true;
    writeState({ status: 'subscribed', at: Date.now() });
    if (this.formStep) this.formStep.hidden = true;
    if (this.successStep) this.successStep.hidden = false;
  }

  /**
   * Iscrizione senza ricaricare la pagina. Shopify protegge il form con un captcha invisibile (hCaptcha): al submit genera il
   * token e poi chiama form.submit(). Quel submit() e intercettato (send) e il form parte in background con il token, cosi il
   * popup resta aperto e mostra il codice. Se il captcha non e attivo si invia direttamente.
   */
  submit(event) {
    if (!this.form.checkValidity()) return;
    if (this.sending) {
      event.preventDefault();
      return;
    }
    if (this.errorMessage) this.errorMessage.hidden = true;
    const protectedByCaptcha = this.form.dataset.hcaptchaBound || this.form.dataset.recaptchaBound || this.form.dataset.cptcha;
    if (!protectedByCaptcha) {
      event.preventDefault();
      this.send();
    }
  }

  async send() {
    if (this.sending) return;
    this.sending = true;
    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (response.ok && response.url.includes('customer_posted=true')) {
        this.showSuccess();
        this.successStep?.focus();
        return;
      }
      if (response.ok && !response.url.includes('/challenge')) {
        // Pagina restituita con gli errori del form (per esempio email non valida)
        if (this.errorMessage) this.errorMessage.hidden = false;
        return;
      }
    } catch (error) {
      /* rete o risposta bloccata: si passa all'invio normale */
    } finally {
      this.sending = false;
      if (button) button.disabled = false;
    }
    if (this.subscribed || (this.errorMessage && !this.errorMessage.hidden)) return;
    // Verifica aggiuntiva richiesta da Shopify: invio normale, al ritorno (?customer_posted=true) il popup mostra il codice
    HTMLFormElement.prototype.submit.call(this.form);
  }

  async copyCode() {
    const code = this.querySelector('[data-popup-code]')?.textContent.trim();
    if (!code) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (error) {
      try {
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('[data-popup-code]'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        copied = document.execCommand('copy');
      } catch (fallbackError) {
        copied = false;
      }
    }
    if (copied) {
      this.copyButton.textContent = this.copyButton.dataset.copied;
      if (this.status) this.status.textContent = this.copyButton.dataset.copied;
      window.setTimeout(() => {
        this.copyButton.textContent = this.copyButton.dataset.label;
      }, 2500);
    }
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
